import { createTheme } from "@mui/material/styles";

import { palette } from './palette';
import { shadows } from './shadows';
import {typography} from './typography';

export const lightTheme = createTheme({
    palette: palette('light'),
    shadows: shadows(),
    shape: { borderRadius: 8 },
    typography,
    components: {
        MuiChip: {
            styleOverrides: {
                root: {
                    height: '24px',
                    borderRadius: '6px',
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    transition: 'all 0.2s ease',
                    border: '1px solid',
                    borderColor: 'rgba(0, 0, 0, 0.12)',
                    backgroundColor: 'transparent',
                    boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                    '&.MuiChip-clickable:hover, &.MuiChip-deletable:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                    }
                },
                label: {
                    paddingLeft: '10px',
                    paddingRight: '10px',
                },
                filled: {
                    backgroundColor: 'rgba(0, 0, 0, 0.05)',
                    border: '1px solid rgba(0, 0, 0, 0.2)',
                    boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                    '&.MuiChip-clickable:hover, &.MuiChip-deletable:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.08)',
                        borderColor: 'rgba(0, 0, 0, 0.25)',
                        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                    }
                },
                outlined: {
                    backgroundColor: 'transparent',
                    borderColor: 'rgba(0, 0, 0, 0.12)',
                    boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                    '&.MuiChip-clickable:hover, &.MuiChip-deletable:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                        borderColor: 'rgba(0, 0, 0, 0.2)',
                        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                    }
                },
                colorPrimary: {
                    '&.MuiChip-filled': {
                        backgroundColor: 'rgba(31, 46, 62, 0.1)',
                        color: '#1F2E3E',
                        border: '1px solid rgba(31, 46, 62, 0.2)',
                        '&.MuiChip-clickable:hover, &.MuiChip-deletable:hover': {
                            backgroundColor: 'rgba(31, 46, 62, 0.15)',
                            borderColor: 'rgba(31, 46, 62, 0.25)',
                        }
                    },
                    '&.MuiChip-outlined': {
                        color: '#1F2E3E',
                        borderColor: 'rgba(31, 46, 62, 0.3)',
                        '&.MuiChip-clickable:hover, &.MuiChip-deletable:hover': {
                            backgroundColor: 'rgba(31, 46, 62, 0.05)',
                            borderColor: 'rgba(31, 46, 62, 0.4)',
                        }
                    }
                },
                colorSuccess: {
                    '&.MuiChip-filled': {
                        backgroundColor: 'rgba(76, 175, 80, 0.1)',
                        color: '#2e7d32',
                        border: '1px solid rgba(76, 175, 80, 0.2)',
                        '&.MuiChip-clickable:hover, &.MuiChip-deletable:hover': {
                            backgroundColor: 'rgba(76, 175, 80, 0.15)',
                            borderColor: 'rgba(76, 175, 80, 0.25)',
                        }
                    },
                    '&.MuiChip-outlined': {
                        color: '#2e7d32',
                        borderColor: 'rgba(76, 175, 80, 0.3)',
                        '&.MuiChip-clickable:hover, &.MuiChip-deletable:hover': {
                            backgroundColor: 'rgba(76, 175, 80, 0.05)',
                            borderColor: 'rgba(76, 175, 80, 0.4)',
                        }
                    }
                },
                colorError: {
                    '&.MuiChip-filled': {
                        backgroundColor: 'rgba(211, 47, 47, 0.1)',
                        color: '#c62828',
                        border: '1px solid rgba(211, 47, 47, 0.2)',
                        '&.MuiChip-clickable:hover, &.MuiChip-deletable:hover': {
                            backgroundColor: 'rgba(211, 47, 47, 0.15)',
                            borderColor: 'rgba(211, 47, 47, 0.25)',
                        }
                    },
                    '&.MuiChip-outlined': {
                        color: '#c62828',
                        borderColor: 'rgba(211, 47, 47, 0.3)',
                        '&.MuiChip-clickable:hover, &.MuiChip-deletable:hover': {
                            backgroundColor: 'rgba(211, 47, 47, 0.05)',
                            borderColor: 'rgba(211, 47, 47, 0.4)',
                        }
                    }
                },
                colorWarning: {
                    '&.MuiChip-filled': {
                        backgroundColor: 'rgba(255, 152, 0, 0.1)',
                        color: '#e65100',
                        border: '1px solid rgba(255, 152, 0, 0.2)',
                        '&.MuiChip-clickable:hover, &.MuiChip-deletable:hover': {
                            backgroundColor: 'rgba(255, 152, 0, 0.15)',
                            borderColor: 'rgba(255, 152, 0, 0.25)',
                        }
                    },
                    '&.MuiChip-outlined': {
                        color: '#e65100',
                        borderColor: 'rgba(255, 152, 0, 0.3)',
                        '&.MuiChip-clickable:hover, &.MuiChip-deletable:hover': {
                            backgroundColor: 'rgba(255, 152, 0, 0.05)',
                            borderColor: 'rgba(255, 152, 0, 0.4)',
                        }
                    }
                },
                colorInfo: {
                    '&.MuiChip-filled': {
                        backgroundColor: 'rgba(33, 150, 243, 0.1)',
                        color: '#0277bd',
                        border: '1px solid rgba(33, 150, 243, 0.2)',
                        '&.MuiChip-clickable:hover, &.MuiChip-deletable:hover': {
                            backgroundColor: 'rgba(33, 150, 243, 0.15)',
                            borderColor: 'rgba(33, 150, 243, 0.25)',
                        }
                    },
                    '&.MuiChip-outlined': {
                        color: '#0277bd',
                        borderColor: 'rgba(33, 150, 243, 0.3)',
                        '&.MuiChip-clickable:hover, &.MuiChip-deletable:hover': {
                            backgroundColor: 'rgba(33, 150, 243, 0.05)',
                            borderColor: 'rgba(33, 150, 243, 0.4)',
                        }
                    }
                },
                deleteIcon: {
                    fontSize: '16px',
                    marginLeft: '-4px',
                    marginRight: '4px',
                    color: 'inherit',
                    opacity: 0.5,
                    '&:hover': {
                        opacity: 0.8,
                    }
                }
            }
        },
        MuiDialog: {
            styleOverrides: {
                paper: {
                    background: 'linear-gradient(to bottom, rgba(255, 255, 255, 1), rgba(252, 252, 252, 1))',
                    boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.40), 0 4px 6px -2px rgba(0, 0, 0, 0.15)',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '@keyframes scaleIn': {
                        from: { transform: 'scale(0.95)', opacity: 0 },
                        to: { transform: 'scale(1)', opacity: 1 }
                    },
                    animation: 'scaleIn 0.2s ease-out',
                },
                root: {
                    '& .MuiBackdrop-root': {
                        backgroundColor: 'rgba(0, 0, 0, 0.2)',
                        backdropFilter: 'blur(1px)',
                        WebkitBackdropFilter: 'blur(1px)',
                    }
                }
            }
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    overflow: 'visible',
                }
            }
        },
        MuiCssBaseline: {
            styleOverrides: {
                '*': {
                    // Webkit browsers (Chrome, Safari, Edge)
                    '&::-webkit-scrollbar': {
                        width: '8px',
                        height: '8px',
                    },
                    '&::-webkit-scrollbar-track': {
                        background: 'transparent',
                        borderRadius: '4px',
                        '&:hover': {
                            background: 'rgba(0, 0, 0, 0.02)',
                        },
                    },
                    '&::-webkit-scrollbar-thumb': {
                        background: 'rgba(0, 0, 0, 0.2)',
                        borderRadius: '4px',
                        transition: 'background 0.2s ease',
                        '&:hover': {
                            background: 'rgba(0, 0, 0, 0.3)',
                        },
                    },
                    // Firefox
                    scrollbarWidth: 'thin',
                    scrollbarColor: 'rgba(0, 0, 0, 0.2) transparent',
                },
            },
        }
    }
})

export const darkTheme = createTheme({
    palette: palette('dark'),
    shadows: shadows(),
    shape: { borderRadius: 8 },
    typography,
    components: {
        MuiChip: {
            styleOverrides: {
                root: {
                    height: '24px',
                    borderRadius: '6px',
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    transition: 'all 0.2s ease',
                    border: '1px solid',
                    borderColor: 'rgba(255, 255, 255, 0.12)',
                    backgroundColor: 'transparent',
                    boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.1)',
                    '&.MuiChip-clickable:hover, &.MuiChip-deletable:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.04)',
                        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.2)',
                    }
                },
                label: {
                    paddingLeft: '10px',
                    paddingRight: '10px',
                },
                filled: {
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.1)',
                    '&.MuiChip-clickable:hover, &.MuiChip-deletable:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.08)',
                        borderColor: 'rgba(255, 255, 255, 0.25)',
                        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.2)',
                    }
                },
                outlined: {
                    backgroundColor: 'transparent',
                    borderColor: 'rgba(255, 255, 255, 0.12)',
                    boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.1)',
                    '&.MuiChip-clickable:hover, &.MuiChip-deletable:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.04)',
                        borderColor: 'rgba(255, 255, 255, 0.2)',
                        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.2)',
                    }
                },
                colorPrimary: {
                    '&.MuiChip-filled': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        color: '#ffffff',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        '&.MuiChip-clickable:hover, &.MuiChip-deletable:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.15)',
                            borderColor: 'rgba(255, 255, 255, 0.25)',
                        }
                    },
                    '&.MuiChip-outlined': {
                        color: '#ffffff',
                        borderColor: 'rgba(255, 255, 255, 0.3)',
                        '&.MuiChip-clickable:hover, &.MuiChip-deletable:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                            borderColor: 'rgba(255, 255, 255, 0.4)',
                        }
                    }
                },
                colorSuccess: {
                    '&.MuiChip-filled': {
                        backgroundColor: 'rgba(76, 175, 80, 0.15)',
                        color: '#66bb6a',
                        border: '1px solid rgba(76, 175, 80, 0.25)',
                        '&.MuiChip-clickable:hover, &.MuiChip-deletable:hover': {
                            backgroundColor: 'rgba(76, 175, 80, 0.2)',
                            borderColor: 'rgba(76, 175, 80, 0.3)',
                        }
                    },
                    '&.MuiChip-outlined': {
                        color: '#66bb6a',
                        borderColor: 'rgba(76, 175, 80, 0.35)',
                        '&.MuiChip-clickable:hover, &.MuiChip-deletable:hover': {
                            backgroundColor: 'rgba(76, 175, 80, 0.08)',
                            borderColor: 'rgba(76, 175, 80, 0.45)',
                        }
                    }
                },
                colorError: {
                    '&.MuiChip-filled': {
                        backgroundColor: 'rgba(244, 67, 54, 0.15)',
                        color: '#ef5350',
                        border: '1px solid rgba(244, 67, 54, 0.25)',
                        '&.MuiChip-clickable:hover, &.MuiChip-deletable:hover': {
                            backgroundColor: 'rgba(244, 67, 54, 0.2)',
                            borderColor: 'rgba(244, 67, 54, 0.3)',
                        }
                    },
                    '&.MuiChip-outlined': {
                        color: '#ef5350',
                        borderColor: 'rgba(244, 67, 54, 0.35)',
                        '&.MuiChip-clickable:hover, &.MuiChip-deletable:hover': {
                            backgroundColor: 'rgba(244, 67, 54, 0.08)',
                            borderColor: 'rgba(244, 67, 54, 0.45)',
                        }
                    }
                },
                colorWarning: {
                    '&.MuiChip-filled': {
                        backgroundColor: 'rgba(255, 152, 0, 0.15)',
                        color: '#ffa726',
                        border: '1px solid rgba(255, 152, 0, 0.25)',
                        '&.MuiChip-clickable:hover, &.MuiChip-deletable:hover': {
                            backgroundColor: 'rgba(255, 152, 0, 0.2)',
                            borderColor: 'rgba(255, 152, 0, 0.3)',
                        }
                    },
                    '&.MuiChip-outlined': {
                        color: '#ffa726',
                        borderColor: 'rgba(255, 152, 0, 0.35)',
                        '&.MuiChip-clickable:hover, &.MuiChip-deletable:hover': {
                            backgroundColor: 'rgba(255, 152, 0, 0.08)',
                            borderColor: 'rgba(255, 152, 0, 0.45)',
                        }
                    }
                },
                colorInfo: {
                    '&.MuiChip-filled': {
                        backgroundColor: 'rgba(33, 150, 243, 0.15)',
                        color: '#42a5f5',
                        border: '1px solid rgba(33, 150, 243, 0.25)',
                        '&.MuiChip-clickable:hover, &.MuiChip-deletable:hover': {
                            backgroundColor: 'rgba(33, 150, 243, 0.2)',
                            borderColor: 'rgba(33, 150, 243, 0.3)',
                        }
                    },
                    '&.MuiChip-outlined': {
                        color: '#42a5f5',
                        borderColor: 'rgba(33, 150, 243, 0.35)',
                        '&.MuiChip-clickable:hover, &.MuiChip-deletable:hover': {
                            backgroundColor: 'rgba(33, 150, 243, 0.08)',
                            borderColor: 'rgba(33, 150, 243, 0.45)',
                        }
                    }
                },
                deleteIcon: {
                    fontSize: '16px',
                    marginLeft: '4px',
                    marginRight: '2px',
                    color: 'inherit',
                    opacity: 0.5,
                    '&:hover': {
                        opacity: 0.8,
                    }
                }
            }
        },
        MuiDialog: {
            styleOverrides: {
                paper: {
                    background: 'linear-gradient(to bottom, rgba(22, 28, 36, 1), rgba(20, 25, 31, 1))',
                    boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.60), 0 4px 6px -2px rgba(0, 0, 0, 0.25)',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '@keyframes scaleIn': {
                        from: { transform: 'scale(0.95)', opacity: 0 },
                        to: { transform: 'scale(1)', opacity: 1 }
                    },
                    animation: 'scaleIn 0.2s ease-out',
                },
                root: {
                    '& .MuiBackdrop-root': {
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        backdropFilter: 'blur(1px)',
                        WebkitBackdropFilter: 'blur(1px)',
                    }
                }
            }
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    overflow: 'visible',
                }
            }
        },
        MuiCssBaseline: {
            styleOverrides: {
                '*': {
                    // Webkit browsers (Chrome, Safari, Edge)
                    '&::-webkit-scrollbar': {
                        width: '8px',
                        height: '8px',
                    },
                    '&::-webkit-scrollbar-track': {
                        background: 'transparent',
                        borderRadius: '4px',
                        '&:hover': {
                            background: 'rgba(255, 255, 255, 0.05)',
                        },
                    },
                    '&::-webkit-scrollbar-thumb': {
                        background: 'rgba(255, 255, 255, 0.2)',
                        borderRadius: '4px',
                        transition: 'background 0.2s ease',
                        '&:hover': {
                            background: 'rgba(255, 255, 255, 0.3)',
                        },
                    },
                    // Firefox
                    scrollbarWidth: 'thin',
                    scrollbarColor: 'rgba(255, 255, 255, 0.2) transparent',
                },
            },
        }
    }
});